import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { ProgressRing, ProgressBar } from "@/components/common/Progress";
import { RagBadge } from "@/components/common/RagBadge";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  fmtDate, fmtValue, RAG, scoreRag, ACTION_STATUS, PRIORITY_STATUS, PLAN_STATUS, relDays,
} from "@/lib/format";
import { AI_PROMPTS } from "@/lib/constants";
import { TeamDeliveryCard } from "@/components/workspace/TeamDeliveryCard";
import { PeopleTodayCard } from "@/components/people/PeopleTodayCard";

function SectionCard({ title, icon, action, children, testId, className = "" }) {
  const Icon = icon ? Icons[icon] : null;
  return (
    <div className={`amden-card p-6 ${className}`} data-testid={testId}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon size={17} className="text-[#FFB000]" strokeWidth={2} />}
          <h3 className="font-display text-base font-bold text-[#0B1729]">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { orgId } = useOrg();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ask, setAsk] = useState("");
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    setData(null);
    api.get(`/dashboard?org_id=${orgId}`).then((r) => setData(r.data)).catch(() => {});
  }, [orgId]);

  if (!data) return <PageLoading />;

  const hour = dayjs().hour();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const h = data.health;

  const submitAsk = (e) => {
    e.preventDefault();
    if (!ask.trim()) return;
    navigate("/ask-amden", { state: { prompt: ask.trim() } });
  };

  const summaryBits = [];
  if (data.actions.overdue) summaryBits.push(`${data.actions.overdue} overdue action${data.actions.overdue > 1 ? "s" : ""}`);
  if (data.plan?.at_risk) summaryBits.push(`${data.plan.at_risk} priorit${data.plan.at_risk > 1 ? "ies" : "y"} at risk`);
  const redKpis = (data.kpi_snapshot || []).filter((k) => k.status === "red").length;
  if (redKpis) summaryBits.push(`${redKpis} KPI${redKpis > 1 ? "s" : ""} off target`);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Greeting */}
      <div>
        <div className="label-eyebrow mb-1.5">{dayjs().format("dddd, DD MMMM YYYY")}</div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#0B1729] sm:text-[28px]">
          {greeting}, {data.user_first_name}.{" "}
          <span className="text-[#1E2D44]/70">
            {summaryBits.length
              ? `Here's where your business needs attention this week.`
              : `Your business is on track this week — keep the momentum.`}
          </span>
        </h1>
        {summaryBits.length > 0 && (
          <p className="mt-1.5 text-sm text-[#1E2D44]/60">
            Priorities today: {summaryBits.join(", ")}.
          </p>
        )}
      </div>

      {/* Ask Amden bar */}
      <form onSubmit={submitAsk} className="amden-card overflow-hidden p-0" data-testid="ask-amden-bar">
        <div className="flex items-center gap-3 border-b border-[#E4E8EF] bg-[#0B1729] px-5 py-3">
          <Icons.Sparkles size={18} className="text-[#FFC24D]" />
          <span className="font-display text-sm font-semibold text-white">Ask Amden</span>
          <span className="text-xs text-white/40">What would you like help with today?</span>
        </div>
        <div className="flex items-center gap-2 p-3">
          <Input
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            placeholder="e.g. What should I focus on this week?"
            className="h-11 border-0 bg-transparent text-sm focus-visible:ring-0"
            data-testid="ask-amden-input"
          />
          <Button type="submit" className="h-11 shrink-0 bg-[#FFB000] px-5 text-[#0B1729] hover:bg-[#E69E00]" data-testid="ask-amden-submit">
            <Icons.ArrowUp size={18} />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 px-3 pb-3">
          {AI_PROMPTS.slice(0, 4).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => navigate("/ask-amden", { state: { prompt: p } })}
              className="rounded-full border border-[#E4E8EF] bg-white px-3 py-1 text-xs text-[#1E2D44]/70 transition-colors hover:border-[#FFC24D] hover:text-[#FFB000]"
            >
              {p}
            </button>
          ))}
        </div>
      </form>

      {/* Action stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-due-today" icon="CalendarClock" label="Due today" value={data.actions.due_today} tone="amber" onClick={() => navigate("/actions?due=today")} />
        <StatCard testId="stat-due-week" icon="CalendarDays" label="Due this week" value={data.actions.due_week} tone="blue" onClick={() => navigate("/actions?due=week")} />
        <StatCard testId="stat-overdue" icon="TriangleAlert" label="Overdue" value={data.actions.overdue} tone={data.actions.overdue ? "red" : "green"} onClick={() => navigate("/actions?scope=overdue")} />
        <StatCard testId="stat-completed" icon="CircleCheck" label="Completed this week" value={data.actions.completed_week} tone="green" onClick={() => navigate("/actions?status=completed")} />
      </div>

      {data.team_delivery && <TeamDeliveryCard d={data.team_delivery} />}
      {data.people_today && <PeopleTodayCard d={data.people_today} />}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Business Health */}
        <SectionCard title="Business Health" icon="Activity" testId="card-health"
          action={<button onClick={() => navigate("/business-health")} className="text-xs font-medium text-[#FFB000] hover:underline">View</button>}>
          {h ? (
            <div className="flex flex-col items-center">
              <ScoreGauge score={h.overall} size={158} />
              <div className="mt-3 flex items-center gap-1.5 text-sm">
                {h.movement >= 0 ? (
                  <Icons.TrendingUp size={15} className="text-[#3D8B65]" />
                ) : (
                  <Icons.TrendingDown size={15} className="text-[#C95555]" />
                )}
                <span className={h.movement >= 0 ? "text-[#3D8B65]" : "text-[#C95555]"}>
                  {h.movement >= 0 ? "+" : ""}{h.movement} pts since last review
                </span>
              </div>
              <div className="mt-4 w-full space-y-2 border-t border-[#EEF1F6] pt-4 text-sm">
                <div className="flex justify-between"><span className="text-[#1E2D44]/55">Strongest</span><span className="font-medium text-[#3D8B65]">{h.strongest}</span></div>
                <div className="flex justify-between"><span className="text-[#1E2D44]/55">Weakest</span><span className="font-medium text-[#C95555]">{h.weakest}</span></div>
                <div className="flex justify-between"><span className="text-[#1E2D44]/55">Next review</span><span className="font-medium">{fmtDate(h.next_date)}</span></div>
              </div>
            </div>
          ) : (
            <EmptyState icon="Activity" title="No assessment yet" description="Complete a Business Health Assessment to see your score." />
          )}
        </SectionCard>

        {/* My Plan */}
        <SectionCard title="My Plan" icon="Target" testId="card-plan"
          action={<button onClick={() => navigate("/plan")} className="text-xs font-medium text-[#FFB000] hover:underline">Open</button>}>
          {data.plan ? (
            <div>
              <button onClick={() => setBreakdownOpen(true)} className="-m-1 flex w-full items-center gap-4 rounded-lg p-1 text-left transition-colors hover:bg-[#F4F6F9]" data-testid="open-plan-breakdown">
                <ProgressRing value={data.plan.percent} size={84} stroke={8} />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#0B1729]">
                    {data.plan.plan_count} plan{data.plan.plan_count === 1 ? "" : "s"}
                  </div>
                  <div className="mt-1 text-xs text-[#1E2D44]/55">
                    {data.plan.active_count} active · {data.plan.total_priorities} priorit{data.plan.total_priorities === 1 ? "y" : "ies"}
                  </div>
                  <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-[#B87F1E]">View each plan <Icons.ChevronRight size={12} /></div>
                </div>
              </button>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-[#3D8B65]/8 p-3" data-testid="plan-on-track">
                  <div className="text-xl font-bold text-[#3D8B65]">{data.plan.on_track}</div>
                  <div className="text-xs text-[#1E2D44]/55">On track</div>
                </div>
                <div className="rounded-lg bg-[#D99A32]/10 p-3" data-testid="plan-at-risk">
                  <div className="text-xl font-bold text-[#B87F1E]">{data.plan.at_risk}</div>
                  <div className="text-xs text-[#1E2D44]/55">At risk</div>
                </div>
              </div>
              {data.priorities.length > 0 && (
                <div className="mt-4 space-y-3 border-t border-[#EEF1F6] pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#1E2D44]/40">Needs attention</div>
                  {data.priorities.slice(0, 3).map((p) => (
                    <div key={p.id} data-testid={`needs-attention-${p.id}`}>
                      <div className="mb-0.5 flex items-center justify-between text-xs">
                        <span className="truncate font-medium text-[#0B1729]">{p.title}</span>
                        <span className="ml-2 shrink-0 text-[#1E2D44]/50">{p.percent_complete}%</span>
                      </div>
                      {p.plan_title && <div className="mb-1 truncate text-[10px] text-[#1E2D44]/40">{p.plan_title}</div>}
                      <ProgressBar value={p.percent_complete} color={RAG[PRIORITY_STATUS[p.status]?.rag || "grey"].solid} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <EmptyState icon="Target" title="No plans yet" description="Create a plan to focus the period ahead." />
          )}
        </SectionCard>

        {/* Next coaching session */}
        <SectionCard title="Next Coaching Session" icon="Users" testId="card-session"
          action={<button onClick={() => navigate("/coaching")} className="text-xs font-medium text-[#FFB000] hover:underline">All</button>}>
          {data.next_session ? (
            <div>
              <div className="rounded-xl bg-[#0B1729] p-4 text-white">
                <div className="text-xs text-white/50">{fmtDate(data.next_session.date)} · {dayjs(data.next_session.date).format("HH:mm")}</div>
                <div className="mt-1 font-display text-lg font-bold">{data.next_session.title}</div>
                <div className="mt-1 text-sm text-white/60">with {data.next_session.consultant}</div>
              </div>
              <p className="mt-3 text-sm text-[#1E2D44]/65">{data.next_session.objective}</p>
              {data.next_session.status === "preparation_required" && (
                <div className="mt-2"><RagBadge rag="amber">Preparation required</RagBadge></div>
              )}
              <Button
                onClick={() => navigate(`/coaching/${data.next_session.id}`)}
                variant="outline"
                className="mt-4 w-full border-[#E4E8EF] hover:border-[#FFB000] hover:text-[#FFB000]"
                data-testid="view-agenda-btn"
              >
                <Icons.ClipboardList size={16} className="mr-2" /> View agenda & prep
              </Button>
            </div>
          ) : (
            <EmptyState icon="CalendarPlus" title="No session scheduled" description="Your consultant will schedule the next session." />
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* This Week */}
        <SectionCard title="This Week" icon="CalendarCheck" testId="card-this-week" className="lg:col-span-1">
          {data.this_week.length ? (
            <div className="space-y-3">
              {data.this_week.map((a) => (
                <div key={a.id} className="flex items-start gap-3 rounded-lg border border-[#EEF1F6] p-3">
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${RAG[ACTION_STATUS[a.status]?.rag || "grey"].dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-[#0B1729]">{a.title}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-[#1E2D44]/50">
                      <span>{a.owner_name}</span>
                      <span>·</span>
                      <span className={relDays(a.due_date) < 0 ? "font-medium text-[#C95555]" : ""}>Due {fmtDate(a.due_date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon="CalendarCheck" title="Nothing urgent" description="No priorities due this week." />
          )}
        </SectionCard>

        {/* Attention required */}
        <SectionCard title="Attention Required" icon="TriangleAlert" testId="card-attention" className="lg:col-span-1">
          {data.attention.length ? (
            <div className="space-y-2">
              {data.attention.map((item, i) => (
                <button
                  key={i}
                  onClick={() => navigate(item.link)}
                  className="flex w-full items-center gap-3 rounded-lg border border-[#EEF1F6] p-3 text-left transition-colors hover:border-[#FFC24D] hover:bg-[#F4F6F9]"
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full ${RAG[item.severity].dot}`} />
                  <span className="flex-1 text-sm text-[#1E2D44]">{item.text}</span>
                  <Icons.ChevronRight size={15} className="text-[#1E2D44]/30" />
                </button>
              ))}
            </div>
          ) : (
            <EmptyState icon="CircleCheck" title="All clear" description="Nothing needs your attention right now." />
          )}
        </SectionCard>

        {/* KPI snapshot */}
        <SectionCard title="KPI Snapshot" icon="Gauge" testId="card-kpi"
          action={<button onClick={() => navigate("/scorecard")} className="text-xs font-medium text-[#FFB000] hover:underline">Scorecard</button>}
          className="lg:col-span-1">
          {data.kpi_snapshot.length ? (
            <div className="space-y-2.5">
              {data.kpi_snapshot.map((k) => (
                <div key={k.id} className="flex items-center justify-between rounded-lg border border-[#EEF1F6] p-3">
                  <div>
                    <div className="text-sm font-medium text-[#0B1729]">{k.name}</div>
                    <div className="text-xs text-[#1E2D44]/50">Target {fmtValue(k.target, k.unit)}</div>
                  </div>
                  <div className="flex items-center gap-2 text-right">
                    <div>
                      <div className="text-sm font-bold text-[#0B1729]">{fmtValue(k.current_value, k.unit)}</div>
                    </div>
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full ${RAG[k.status]?.bg || RAG.grey.bg}`}>
                      {k.trend === "up" ? <Icons.TrendingUp size={13} className={RAG[k.status]?.text} /> : k.trend === "down" ? <Icons.TrendingDown size={13} className={RAG[k.status]?.text} /> : <Icons.Minus size={13} className={RAG[k.status]?.text} />}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon="Gauge" title="No KPIs" description="Add KPIs to track performance." />
          )}
        </SectionCard>
      </div>

      <Dialog open={breakdownOpen} onOpenChange={setBreakdownOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="plan-breakdown-dialog">
          <DialogHeader><DialogTitle>Plans breakdown</DialogTitle><DialogDescription>Progress across every plan for this client.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {(data.plan?.plans || []).map((pl) => (
              <button key={pl.id} onClick={() => { setBreakdownOpen(false); navigate(`/plan/${pl.id}`); }}
                className="w-full rounded-xl border border-[#EEF1F6] p-4 text-left transition-colors hover:border-[#FFC24D] hover:bg-[#F4F6F9]"
                data-testid={`breakdown-plan-${pl.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <RagBadge rag={PLAN_STATUS[pl.status]?.rag}>{PLAN_STATUS[pl.status]?.label || pl.status}</RagBadge>
                      <span className="text-xs text-[#1E2D44]/45">{pl.priority_count} priorit{pl.priority_count === 1 ? "y" : "ies"}</span>
                    </div>
                    <div className="mt-1.5 truncate font-display text-sm font-bold text-[#0B1729]">{pl.title}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-bold text-[#0B1729]">{pl.percent}%</div>
                    <div className="text-[11px] text-[#1E2D44]/50">{pl.on_track} on track · {pl.at_risk} at risk</div>
                  </div>
                </div>
                <div className="mt-2.5"><ProgressBar value={pl.percent} color={RAG[PLAN_STATUS[pl.status]?.rag || "grey"].solid} /></div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
